import { useCallback, useEffect, useState, useMemo } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import ListFormPageHeader from "../../components/ListFormPageHeader";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import { openPdfResponse } from "../../utils/openPdfInApp";
import { useNavigate } from "react-router-dom";
import "../../App.css";
import Sidebar from '../../Sidebar';
import Swal from "sweetalert2";
import { useCookies } from 'react-cookie';
import { displayError, getApiErrorMessage } from "../../helpers";
import { getAllGoodsReceiptNotes, printGoodsReceiptNote } from "../../services/GoodsReceiptNoteServices";
import { getListViewSettings } from "../../services/SettingsServices";
import { buildGoodsReceiptNoteQueryParams, DEFAULT_LIST_VIEW_CONFIG } from "../../utils/listViewDateRanges";
import ListPageGradientSummary from "../../components/ListPageGradientSummary";
import { buildGradientCard, formatRecordStatus, splitRecordsByStatus, sumField, formatQty } from "../../utils/listSummaryUtils";
import moment from 'moment';

function GoodsReceiptNoteTable() {
    const InitialData = {
        records: []
    };
    const [data, setData] = useState(InitialData);
    const navigate = useNavigate(); 
    const [refreshKey, setRefreshKey] = useState(0);
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(false);
    const left_pinned = ["plant","grn_no"]
    const right_pinned = ["id"]  

    const todayDateLabel = moment().format('DD-MM-YYYY');

    const { active: activeRecords, cancelled: cancelledRecords } = useMemo(
        () => splitRecordsByStatus(data.records),
        [data.records],
    );

    const highlightCard = useMemo(() => {
        const todayCount = data.records.filter(
            (row) => row.goods_receipt_note_date === todayDateLabel,
        ).length;
        return buildGradientCard('Today GRN', todayCount, 'today');
    }, [data.records, todayDateLabel]);

    const summaryCards = useMemo(() => {
        const cards = [
            buildGradientCard('Total GRN', data.records.length, 'blue'),
            buildGradientCard('Received Qty', formatQty(sumField(activeRecords, 'received_qty')), 'purple'),
            buildGradientCard('DC Qty', formatQty(sumField(activeRecords, 'dc_qty')), 'teal'),
            buildGradientCard('Accepted Qty', formatQty(sumField(activeRecords, 'accepted_qty')), 'blue'),
        ];
        const cancelledQty = sumField(cancelledRecords, 'received_qty');
        if (cancelledRecords.length && cancelledQty > 0) {
            cards.push(buildGradientCard('Cancelled Rcvd Qty', formatQty(cancelledQty), 'red'));
        }
        return cards;
    }, [data.records.length, activeRecords, cancelledRecords]);

    const columns = useMemo(
        () => [
            {
                fieldName: "plant",
                headerName: 'Plant'
            },
            {
                fieldName: "grn_no",
                headerName: 'GRN No.'
            },
            {
                fieldName: "goods_receipt_note_date",
                headerName: 'Date'
            },
            {
                fieldName: 'vendor_name', 
                headerName: 'Vendors Name'
            },
            {
                fieldName: "product_name",
                headerName: 'Material'
            },
            {
                fieldName: "unit",
                headerName: 'Unit'
            },
            {
                fieldName: 'dc_qty', 
                headerName: 'Dc/Invoice Qty'
            },
            {
                fieldName: 'dc_no', 
                headerName: 'DC No'
            },
            {
                fieldName: 'dc_dt', 
                headerName: 'Dc Date'
            },
            {
                fieldName: "vehicle",
                headerName: 'Vehicle No'
            },
            {
                fieldName: "invoice_no",
                headerName: 'Invoice No'
            },
            {
                fieldName: "invoice_date",
                headerName: 'Invoice Date'
            },
            {
                fieldName: "gross_weight",
                headerName: 'Gross Weight'
            },
            {
                fieldName: "tare_weight",
                headerName: 'Tare Weight'
            },
            {
                fieldName: 'received_qty', 
                headerName: 'Received Qty'
            },
            {
                fieldName: 'difference_qty', 
                headerName: 'Difference Qty'
            },
            {
                fieldName: "deduction_qty",
                headerName: 'Deduction Qty'
            },
            {
                fieldName: 'weighment_name', 
                headerName: 'Weighment Name'
            },
            {
                fieldName: 'weighment_slip_no', 
                headerName: 'Weighment No'
            },
            {
                fieldName: "status",
                headerName: 'Status'
            },
            {
                fieldName: "id",
                headerName: "ROW_ACTIONS"
            }
        ],
    [],);

    const onEditGoodsReceiptNote = (grnId, event) => {
       navigate(`/GoodsReceiptNoteEdit/${grnId}/`);
    };

    const handleCSVExport = (event) => {
        console.log('CSV Export');
        exportAsCSV(
            columns.filter(col => col.headerName !== 'ROW_ACTIONS'),
            data.records
        );
    };

    const handlePDFExport = (event) => {
        console.log('PDF Export');
        exportAsPDF(
            'List of Goods Receipt Note',
            columns
            .filter(col => col.headerName !== 'ROW_ACTIONS')
            .map(col=>({header: col.headerName, dataKey: col.fieldName })),
            data.records.map(grn => ({
                id: grn.id,  
                grn_no: grn.grn_no,
                goods_receipt_note_date: grn.goods_receipt_note_date,
                gross_weight: grn.gross_weight,
                tare_weight: grn.tare_weight,
                net_weight: grn.net_weight,
                dc_qty: grn.dc_qty,
                deduction_qty: grn.deduction_qty,
                received_qty: grn.received_qty,
                accepted_qty: grn.accepted_qty,
                dc_no: grn.dc_no,
                dc_dt: grn.dc_dt,
                vendor_name:grn.vendor_name,
                product_name:grn.product_name,
                unit:grn.unit,
                vehicle:grn.vehicle,
                invoice_no: grn.invoice_no,
                invoice_date: grn.invoice_date,
                weighment_name: grn.weighment_name,
                weighment_slip_no: grn.weighment_slip_no,
                difference_qty:grn.difference_qty,
                plant:grn.plant,
                status:grn.status
            })),
            'GRN.pdf'
        );
    };

    const onPrintGoodsReceiptNote = (grnId, event) => {
        event.preventDefault();
        setIsLoading(true);
        printGoodsReceiptNote(cookies, grnId)
        .then(response => {
            openPdfResponse(response, {
                title: 'Goods Receipt Note',
                downloadFileName: 'grn.pdf',
            });
            setIsLoading(false);
        })
        .catch(error => {
            console.log(error);
            displayError(error,'Print Failed');
            setIsLoading(false);
        });  
    };
    const mapGrnRows = (goodsReceiptNoteList) =>
        (goodsReceiptNoteList || []).map((grn) => {
            const isActive = Boolean(grn.status);
            const blank = (value) => (value === null || value === undefined || value === 'undefined' ? '' : value);
            return {
            id: grn.id,
            grn_no: `${grn.prefix || ''}${blank(grn.goods_receipt_note_no)}`,
            goods_receipt_note_date: blank(grn.goods_receipt_note_date),
            gross_weight: blank(grn.gross_weight),
            tare_weight: blank(grn.tare_weight),
            net_weight: blank(grn.net_weight),
            dc_qty: blank(grn.dc_qty),
            deduction_qty: blank(grn.deduction_qty),
            received_qty: blank(grn.received_qty),
            accepted_qty: blank(grn.accepted_qty),
            dc_no: blank(grn.dc_no),
            dc_dt: blank(grn.dc_dt),
            vendor_name: blank(grn.vendor?.name),
            product_name: blank(grn.product?.name),
            unit: blank(grn.product?.unit?.symbol),
            vehicle: grn.transport_mode === 'own'
                ? blank(grn.vehicle?.name)
                : blank(grn.vendor_vehicle_no),
            invoice_no: blank(grn.invoice?.invoice_no),
            invoice_date: blank(grn.invoice?.invoice_date),
            weighment_name: blank(grn.weighment_name),
            weighment_slip_no: blank(grn.weighment_slip_no),
            difference_qty: blank(grn.difference_qty),
            plant: blank(grn.plant?.alias_name || grn.plant_alias),
            status: formatRecordStatus(isActive),
            is_inactive: !isActive,
        };
        });

    const loadGoodsReceiptNotes = useCallback((config) => {
        if (!cookies.myToken) {
            setIsLoading(false);
            return;
        }
        const activeConfig = config || DEFAULT_LIST_VIEW_CONFIG;
        setIsLoading(true);
        getAllGoodsReceiptNotes(cookies, buildGoodsReceiptNoteQueryParams(activeConfig))
        .then((response) => {
            setData({
                records: mapGrnRows(response?.goods_receipt_note_list),
            });
        })
        .catch((error) => {
            displayError(getApiErrorMessage(error), 'Failed to load goods receipt notes');
            setData({ records: [] });
        })
        .finally(() => {
            setIsLoading(false);
        });
    }, [cookies]);

    const handleSettingsSaved = useCallback((config) => {
        loadGoodsReceiptNotes(config);
    }, [loadGoodsReceiptNotes]);

    useEffect(() => {
        if (!cookies.myToken) {
            setIsLoading(false);
            return;
        }
        getListViewSettings(cookies, 'goods_receipt_note')
            .then((response) => {
                loadGoodsReceiptNotes(response.config || DEFAULT_LIST_VIEW_CONFIG);
            })
            .catch(() => {
                loadGoodsReceiptNotes(DEFAULT_LIST_VIEW_CONFIG);
            });
    }, [cookies, cookies.myToken, loadGoodsReceiptNotes, refreshKey]);
    
    return (
        <div id="outer-container"  className="App" > 
            <Sidebar pageWrapId={'page-wrap'} outerContainerId={'outer-container'} />
            <div id="page-wrap">
                <ListFormPageHeader
                    module="STORE"
                    title="List of Goods Receipt Note"
                    viewKey="goods_receipt_note"
                    onSettingsSaved={handleSettingsSaved}
                    formLink="/GoodsReceiptNote"
                    addNewLabel="New Goods Receipt Note"
                />

                <ListPageGradientSummary highlightCard={highlightCard} cards={summaryCards} />

                <div className="container item-list-table-container">
                    <div className="table-responsive">
                        <ReactTableWrapper
                            title='List of Goods Receipt Note'
                            columns={columns}
                            data={data.records}
                            onRowEdit={onEditGoodsReceiptNote}
                            hideDelete={true}
                            onCSVExport={handleCSVExport}
                            onPDFExport={handlePDFExport}
                            isLoading={isLoading}
                            left_pinned={left_pinned}
                            right_pinned={right_pinned}
                            onRowPrint={onPrintGoodsReceiptNote}
                            hidePrint ={false}
                        />
                    </div>
                </div> 
            </div>
            <button style={{position:"relative",right:"40px"}}className="btn btn-fill btn-primary float-end" type="button" onClick={() =>{navigate("/GoodsReceiptNote")}}>Back</button>
        </div>
    );
}
export default GoodsReceiptNoteTable;